package com.eladfinish.bible.contest.screens.pairMatching.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.jsonArray

// The pair-matching lists' models and their parsing from the pair-matching spreadsheet — shared by
// the app and the website generator.

/** Strips asterisk bold-markers: `*word*` → `word`. */
fun String.stripAsterisks(): String = replace(Regex("\\*([^*]+)\\*"), "$1")

interface PairsTopicBase {
    val topic: String
    val source: String
}

@Serializable
data class PairSheetEntry(
    @SerialName("רשימה") val list: String,
    @SerialName("מפתח") val key: String,
    @SerialName("ערך") val value: String,
)

@Serializable
data class PairItem(
    @SerialName("k") val keyItem: String,
    @SerialName("v") val valueItem: String,
)

sealed class PairsTopic : PairsTopicBase

@Serializable
data class SpecialTopicItem(
    @SerialName("topic") override val topic: String,
    @SerialName("source") override val source: String,
) : PairsTopic()

@Serializable
data class PairsTopicItem(
    @SerialName("topic") override val topic: String,
    @SerialName("source") override val source: String,
    @SerialName("key") val keyTitle: String,
    @SerialName("value") val valueTitle: String,
    @SerialName("prev_item") val previousItemDescription: String? = null,
    @SerialName("next_item") val nextItemDescription: String? = null,
    @SerialName("pairList") val pairList: List<PairItem>,
) : PairsTopic() {

    fun getSetOfValues(): List<String> = pairList.map { it.valueItem }.distinct()
}

const val PAIRS_TOPIC_PAIR_LIST = "pairList"

/** The pair-matching sheet's rows (list, key, value) as entries. */
fun List<List<String>>.toPairSheetEntries(): List<PairSheetEntry> =
    map { PairSheetEntry(list = it[0], key = it[1], value = it[2]) }

/** Groups sheet entries into topics, as the JSON [PairsTopicItem] list the game decodes. */
fun List<PairSheetEntry>.toPairsTopicsJson(): String {
    val items = this
    val topicsArray = mutableListOf<Map<String, JsonElement>>()
    var pairsJson = mutableMapOf<String, JsonElement>()

    var type: String? = null
    var key: String
    var value: String

    for (item in items) {
        type = item.list
        key = item.key
        value = item.value

        when {
            type.startsWith("נושא") -> {
                //if the below condition is met,
                //it means we have reached a new section of pair topic;
                //so, push the recent object to the topic array,
                //and initialize a new one.
                if (pairsJson.isNotEmpty()) {
                    topicsArray.add(pairsJson)
                    pairsJson = mutableMapOf()
                }
                //In any case we encounter "topic", init pairList
                pairsJson[PAIRS_TOPIC_PAIR_LIST] = JsonArray(emptyList())
            }

            type.startsWith("תיאור") -> {
                pairsJson["topic"] = JsonPrimitive(key)
                pairsJson["source"] = JsonPrimitive(value)
            }

            type.startsWith("הערה") -> {
                pairsJson["prev_item"] = JsonPrimitive(key)
                pairsJson["next_item"] = JsonPrimitive(value)
            }

            type.startsWith("כותרת") -> {
                pairsJson["key"] = JsonPrimitive(key)
                pairsJson["value"] = JsonPrimitive(value)
            }

            type == "" -> {
                val pair = mutableMapOf<String, JsonPrimitive>()
                pair["k"] = JsonPrimitive(key)
                pair["v"] = JsonPrimitive(value)

                pairsJson[PAIRS_TOPIC_PAIR_LIST] = JsonArray(
                    pairsJson[PAIRS_TOPIC_PAIR_LIST]!!.jsonArray.toMutableList()
                        .apply {
                            add(JsonObject(pair))
                        }
                )
            }
        }
    }
    if (type != null && type == "") {
        topicsArray.add(pairsJson)
    }

    return Json.encodeToString(topicsArray)
}
